import { Box } from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Form from "react-bootstrap/Form";
import {
  deleteTrait,
  getTraits,
} from "../../../services/AssetTraits/assetTraitApisService";
import { DisplayMessage } from "../../../services/helpers";
import AddItemButton from "../../Common/Buttons/AddItemButton.js";
import ColorChip from "../../Common/ColorChip/ColorChip.js";
import Header from "../../Common/Headers/Header.js";
import DeleteConfirmationModal from "../../Common/Modals/DeleteConfirmationModal.js";
import OutletsDropdown from "../../Common/Dropdowns/OutletsDropdown.js";
import TableComponent from "../../Common/Table/TableComponent.js";
import { isValidColor } from "../../../utils/isValidColor.js";
import {
  sortTableColors,
  sortTableStringsNotCaseSensitive,
} from "../../../utils/tableSorting.js";

const AssetTraitsTable = ({ resources, accessibleMoboOutlets, refreshGrid }) => {
  const [allTraits, setAllTraits] = useState([]);
  const [traits, setTraits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTraitId, setSelectedTraitId] = useState(null);
  const [selectedOutletId, setSelectedOutletId] = useState(0);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const history = useHistory();

  const tableSettings = {
    actions: [
      {
        icon: () => <Edit />,
        tooltip: resources["Edit"],
        onClick: (event, rowData) => {
          history.push({
            pathname: `/assettraits/${rowData.id}`,
            state: { traitId: rowData.id },
          });
        },
      },
      {
        icon: () => <DeleteOutline />,
        tooltip: resources["Delete"],
        onClick: (event, rowData) => handleDelete(rowData.id),
      },
    ],
    columns: [
      {
        title: resources["Color"],
        field: "color",
        cellStyle: { width: "10%", minWidth: "80px" },
        headerStyle: { width: "10%" },
        render: (rowData) => (
          <ColorChip
            colorHex={rowData.color}
            id={
              isValidColor(rowData.color)
                ? `color-${rowData.color}-${rowData.id}`
                : `color-none-${rowData.id}`
            }
          />
        ),
        customSort: (a, b) => sortTableColors(a.color, b.color),
      },
      {
        title: resources["Name"],
        field: "name",
        cellStyle: { width: "20%" },
        headerStyle: { width: "20%" },
        customSort: (a, b) => sortTableStringsNotCaseSensitive(a.name, b.name),
      },
      {
        title: resources["Abbreviation"],
        field: "abbreviation",
        cellStyle: { width: "10%", minWidth: "80px" },
        headerStyle: { width: "10%" },
        customSort: (a, b) =>
          sortTableStringsNotCaseSensitive(a.abbreviation, b.abbreviation),
      },
      {
        title: resources["Value"],
        field: "value",
        cellStyle: { width: "25%" },
        headerStyle: { width: "25%" },
        customSort: (a, b) => sortTableStringsNotCaseSensitive(a.value, b.value),
      },
      {
        title: resources["Outlets"],
        field: "outletDescriptions",
        cellStyle: { width: "35%" },
        headerStyle: { width: "35%" },
        customSort: (a, b) =>
          sortTableStringsNotCaseSensitive(
            a.outletDescriptions,
            b.outletDescriptions
          ),
      },
    ],
  };

  const handleDelete = (id) => {
    setSelectedTraitId(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedTraitId(null);
    setDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    if (selectedTraitId) {
      const result = await deleteTrait(selectedTraitId);

      if (result) {
        setAllTraits((prev) => prev.filter((t) => t.id !== selectedTraitId));
        DisplayMessage(resources["TraitWasDeletedWithSuccess"], "success");
      }
    }
    closeDeleteModal();
  };

  const filterTraits = useCallback(
    (outletId) => {
      if (outletId > 0) {
        setTraits(
          allTraits.filter((t) =>
            t.outlets.some((o) => o.outletId === outletId)
          )
        );
      } else {
        setTraits(allTraits);
      }
    },
    [allTraits]
  );

  const fetchTraits = useCallback(async () => {
    setLoading(true);
    const data = await getTraits();
    if (data) {
      const formattedData = data
        .sort((el1, el2) => el1.id - el2.id)
        .map((item) => ({
          id: item.id,
          name: item.name,
          value: item.value,
          abbreviation: item.abbreviation,
          color: item.color,
          outlets: item.outlets ?? [],
          outletDescriptions: (item.outlets ?? [])
            .map((o) => o.description)
            .join(", "),
        }));
      setAllTraits(formattedData);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    filterTraits(selectedOutletId);
  }, [filterTraits, selectedOutletId]);

  useEffect(() => {
    if (isFirstTime || refreshGrid) {
      fetchTraits();
      setIsFirstTime(false);
    }
  }, [fetchTraits, isFirstTime, refreshGrid]);

  return (
    <Box>
      <Box className="position-relative py-4">
        <Header title={resources["AssetTraits"]} loading={loading} />
        {!loading && (
          <>
            <Form.Group className="main-dropdown">
              <OutletsDropdown
                id="outlet-dropdown-grid"
                containerClass="col-md-12"
                resources={resources}
                outlets={accessibleMoboOutlets}
                outlet={selectedOutletId}
                onOutletChange={(e) =>
                  setSelectedOutletId(parseInt(e.target.value))
                }
              />
            </Form.Group>
            <AddItemButton
              title={resources["AddTrait"]}
              linkPath={"/assettraits/new"}
              idName="trait"
            />
            <TableComponent
              resources={resources}
              data={traits}
              tableSettings={tableSettings}
            />
          </>
        )}
        <DeleteConfirmationModal
          onConfirm={confirmDelete}
          resources={resources}
          areYouSureMessage={resources["DeleteTraitUnlinkAssetsWarning"]}
          open={deleteModalOpen}
          onClose={closeDeleteModal}
        />
      </Box>
    </Box>
  );
};

export default AssetTraitsTable;
